import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const Security = () => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('security.title')} | Tap'nGrab</title>
        <meta name="description" content={t('security.metaDescription')} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('security.title')} | Tap'nGrab`} />
        <meta property="og:description" content={t('security.metaDescription')} />
        <meta property="og:url" content="https://tapngrab.ca/security" />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('security.title')} | Tap'nGrab`} />
        <meta name="twitter:description" content={t('security.metaDescription')} />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/security" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl font-bold text-gray-900">{t('security.title')}</h1>
            <p className="mt-2 text-lg text-gray-600">{t('security.subtitle')}</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            
            {/* Reporting Vulnerabilities */}
            <div className="px-6 py-8 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {t('security.reporting.title')}
              </h2>
              <div className="prose prose-lg text-gray-700">
                <p className="mb-4">{t('security.reporting.description')}</p>
                
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>{t('security.reporting.important')}</strong> {t('security.reporting.noPublicIssues')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {t('security.reporting.email.title')}
                    </h3>
                    <p className="text-gray-700 mb-4">{t('security.reporting.email.description')}</p>
                    <a 
                      href="mailto:security@tapngrab.ca"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      security@tapngrab.ca
                    </a>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {t('security.reporting.response.title')}
                    </h3>
                    <p className="text-gray-700 mb-4">{t('security.reporting.response.description')}</p>
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t('security.reporting.response.timeframe')}
                    </div>
                  </div>
                </div>
              </div>
            </div>



            {/* Contact Information */}
            <div className="px-6 py-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {t('security.contact.title')}
              </h2>
              <div className="prose prose-lg text-gray-700">
                <p className="mb-4">{t('security.contact.description')}</p>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <svg className="w-6 h-6 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('security.contact.email')}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-2">
                    <strong>security@tapngrab.ca</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    {t('security.contact.response')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Security;
